import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
// Using basic HTML inputs for simplicity
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/_components/table";
import { AGENT_TYPE, PAGINATION } from "@/constants/constants";
import { useExportTransactionHistoryCSV } from "@/lib/api/hooks";
import { Download, Search, Info, RotateCcw, MessageSquare, BarChart3, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import toast from "react-hot-toast";
import TelemetryDetailsDialog from "@/components/telemetry-details-dialog";
import ConversationDetailsDialog from "@/components/conversation-details-dialog";
import { formatDateTime } from "@/lib/utils";
import { usePageHeader } from "@/contexts/page-header-context";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/_components/badge";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface TransactionData {
  id: number;
  externalCd: string;
  recipientId: string;
  fullName: string;
  channel: string;
  correlationId: string;
  status: string;
  responseTime: number;
  requestAt: string;
  responseAt: string;
  aiResponseTime: number;
  aiRequestAt: string;
  aiResponseAt: string;
}

interface TransactionFilter {
  agentType: string;
  fromDate?: string;
  toDate?: string;
  correlationId?: string;
  externalCd?: string;
  minResponseTime?: number;
  recipientId?: number;
  orderBy?: string;
  sortDirection?: string;
}

const TransactionTrackingPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ANALYTICS_TRANSACTION_TRACK_VIEW">
      <TransactionTrackingContent />
    </PageAccessGuard>
  );
};

const TransactionTrackingContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  // Helper function to get today's date with start of day time in browser timezone
  const getTodayStartOfDay = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Format date in local timezone for datetime-local input
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to get today's date with end of day time in browser timezone
  const getTodayEndOfDay = () => {
    const today = new Date();
    today.setHours(23, 59, 0, 0);

    // Format date in local timezone for datetime-local input
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    agentType: "",
    fromDate: getTodayStartOfDay(), // Today's date with start of day time
    toDate: getTodayEndOfDay(), // Today's date with end of day time
    correlationId: "",
    externalCd: "",
    minResponseTime: "", // Filter for response time greater than x seconds
    recipientId: "", // Filter by recipient ID
  });

  const [telemetryDialogOpen, setTelemetryDialogOpen] = useState(false);
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<number>(0);

  // Use paginated query hook
  const paginatedQuery = usePaginatedQuery<TransactionFilter, TransactionData[]>(
    (filter, options) => api.getTransactionTracking(filter, options),
    {
      pageSize: PAGINATION.DEFAULT_PAGE_SIZE,
    }
  );

  const [selectedPageSize, setSelectedPageSize] = useState(PAGINATION.DEFAULT_PAGE_SIZE);

  const {
    data: transactionData,
    isPending,
    executeQuery,
    currentPage,
    totalCount,
    setCurrentPage,
    currentFilter,
  } = paginatedQuery;

  const pageSize = selectedPageSize;

  const { mutate: exportCSV, isPending: isExporting } = useExportTransactionHistoryCSV({
    mutation: {
      onSuccess: (response) => {
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transaction-tracking-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Transaction data downloaded successfully");
      },
      onError: (error: any) => {
        console.error("Error exporting CSV:", error);
        toast.error("Failed to export CSV");
      },
    },
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.agentType) {
      toast.error("Agent Type is mandatory");
      return false;
    }

    // Either dates or correlation ID must be provided
    const hasDates = formData.fromDate && formData.toDate;
    const hasCorrelationId = formData.correlationId.trim();

    if (!hasDates && !hasCorrelationId) {
      toast.error("Either From/To Date or Correlation ID is mandatory");
      return false;
    }

    return true;
  };

  // Helper function to format datetime with timezone
  const formatDateTimeWithTimezone = (dateTimeString: string) => {
    if (!dateTimeString) return undefined;

    // Create a date object from the input (YYYY-MM-DDTHH:MM format)
    const date = new Date(dateTimeString);

    // Get timezone offset in minutes and convert to hours
    const timezoneOffset = date.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
    const offsetMinutes = Math.abs(timezoneOffset) % 60;
    const offsetSign = timezoneOffset <= 0 ? '+' : '-';

    // Format as ISO string with timezone
    const timezoneString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

    // Return the datetime with seconds and timezone
    return `${dateTimeString}:00${timezoneString}`;
  };

  const handleSearch = useCallback(() => {
    if (!validateForm()) return;

    const filter: TransactionFilter = {
      agentType: formData.agentType,
      orderBy: "responseAt",
      sortDirection: "desc",
      ...(formData.fromDate && { fromDate: formatDateTimeWithTimezone(formData.fromDate) }),
      ...(formData.toDate && { toDate: formatDateTimeWithTimezone(formData.toDate) }),
      ...(formData.correlationId.trim() && { correlationId: formData.correlationId.trim() }),
      ...(formData.externalCd.trim() && { externalCd: formData.externalCd.trim() }),
      ...(formData.minResponseTime && { minResponseTime: parseFloat(formData.minResponseTime) * 1000 }), // Convert seconds to milliseconds
      ...(formData.recipientId && { recipientId: parseInt(formData.recipientId, 10) }),
    };

    executeQuery(filter, 1); // Reset to first page on new search
  }, [formData, executeQuery]);

  const handleDownload = () => {
    if (!validateForm()) {
      toast.error("Please ensure search criteria are valid before downloading");
      return;
    }

    const exportParams = {
      agentType: formData.agentType,
      orderBy: "responseTime",
      sortDirection: "desc",
      ...(formData.fromDate && { fromDate: formatDateTimeWithTimezone(formData.fromDate) }),
      ...(formData.toDate && { toDate: formatDateTimeWithTimezone(formData.toDate) }),
      ...(formData.correlationId.trim() && { correlationId: formData.correlationId.trim() }),
      ...(formData.externalCd.trim() && { externalCd: formData.externalCd.trim() }),
      ...(formData.minResponseTime && { minResponseTime: parseFloat(formData.minResponseTime) * 1000 }), // Convert seconds to milliseconds
      ...(formData.recipientId && { recipientId: parseInt(formData.recipientId, 10) }),
    };

    exportCSV(exportParams);
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined || isNaN(num)) {
      return 'N/A';
    }
    return num.toLocaleString();
  };

  const handleShowTelemetry = (id: number) => {
    setSelectedTransactionId(id);
    setTelemetryDialogOpen(true);
  };

  const handleShowConversation = (id: number) => {
    setSelectedTransactionId(id);
    setConversationDialogOpen(true);
  };

  const handleReset = () => {
    setFormData({
      agentType: "",
      fromDate: getTodayStartOfDay(),
      toDate: getTodayEndOfDay(),
      correlationId: "",
      externalCd: "",
      minResponseTime: "",
      recipientId: "",
    });
    toast.success("Form reset successfully");
  };

  // Use a ref to track if we've already loaded initial data
  const initialLoadRef = useRef(false);

  // Initial load - don't auto-search, wait for user to search
  useEffect(() => {
    if (initialLoadRef.current) {
      return;
    }
    initialLoadRef.current = true;

    // Cleanup function to reset the ref if component unmounts
    return () => {
      initialLoadRef.current = false;
    };
  }, []);

  // Set page header data - only stats, no title
  useEffect(() => {
    setHeaderData({
      stats: transactionData && Array.isArray(transactionData) ? {
        count: totalCount,
        label: 'record',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
    });

    // Cleanup: clear header when component unmounts
    return () => {
      setHeaderData(null);
    };
  }, [transactionData, totalCount, setHeaderData]);

  return (
    <div className="bg-background">
      {/* Main Content */}
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Search & Filter Section - Modern Clean Design */}
          <div className="bg-card border border-border/50 rounded-lg mb-8">
            {/* Compact Header */}
            <div className="px-4 pt-2 pb-4 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find transactions by type, date, or correlation ID
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Filter Controls */}
            <div className="px-3 pb-3">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Agent Type */}
                  <div className="space-y-1.5">
                    <label htmlFor="agentType" className="block text-sm font-semibold text-foreground">
                      Agent Type <span className="text-destructive text-base font-bold">*</span>
                    </label>
                    <Select
                      value={formData.agentType}
                      onValueChange={(value) => handleInputChange("agentType", value)}
                    >
                      <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Select agent type" />
                      </SelectTrigger>
                      <SelectContent>
                        {AGENT_TYPE.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* From Date and Time */}
                  <div className="space-y-1.5">
                    <label htmlFor="fromDate" className="block text-sm font-semibold text-foreground">
                      From Date & Time <span className="text-destructive text-base font-bold">*</span>
                    </label>
                    <input
                      id="fromDate"
                      type="datetime-local"
                      className="h-9 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-primary/60"
                      value={formData.fromDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("fromDate", e.target.value)}
                    />
                  </div>

                  {/* To Date and Time */}
                  <div className="space-y-1.5">
                    <label htmlFor="toDate" className="block text-sm font-semibold text-foreground">
                      To Date & Time <span className="text-destructive">*</span>
                    </label>
                    <input
                      id="toDate"
                      type="datetime-local"
                      className="h-9 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 hover:border-primary/60"
                      value={formData.toDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("toDate", e.target.value)}
                    />
                  </div>

                  {/* Correlation ID */}
                  <div className="space-y-1.5">
                    <label htmlFor="correlationId" className="block text-sm font-semibold text-foreground">
                      Correlation ID <span className="text-destructive text-base font-bold">*</span>
                    </label>
                    <Input
                      id="correlationId"
                      type="text"
                      placeholder="Enter correlation ID"
                      value={formData.correlationId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("correlationId", e.target.value)}
                      className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>

                  {/* External Code */}
                  <div className="space-y-1.5">
                    <label htmlFor="externalCd" className="block text-sm font-semibold text-foreground">
                      External Code
                    </label>
                    <Input
                      id="externalCd"
                      type="text"
                      placeholder="Enter external code"
                      value={formData.externalCd}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("externalCd", e.target.value)}
                      className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>

                  {/* Min Response Time */}
                  <div className="space-y-1.5">
                    <label htmlFor="minResponseTime" className="block text-sm font-semibold text-foreground">
                      Min Response Time (seconds)
                    </label>
                    <Input
                      id="minResponseTime"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="e.g., 2.5"
                      value={formData.minResponseTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("minResponseTime", e.target.value)}
                      className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>

                  {/* Recipient ID */}
                  <div className="space-y-1.5">
                    <label htmlFor="recipientId" className="block text-sm font-semibold text-foreground">
                      Reference No
                    </label>
                    <Input
                      id="recipientId"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Enter recipient ID"
                      value={formData.recipientId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("recipientId", e.target.value)}
                      className="h-9 bg-background border border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-2 pt-2 mt-4">
                  <Button
                    onClick={handleSearch}
                    disabled={isPending}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isPending ? (
                      <>
                        <Search className="h-4 w-4 mr-1.5 animate-pulse" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1.5" />
                        Search
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleDownload}
                    disabled={isExporting}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    {isExporting ? "Exporting..." : "Download CSV"}
                  </Button>

                  <Button
                    onClick={handleReset}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset
                  </Button>
                </div>

                {/* Help Text - Right Aligned */}
                <div className="text-xs text-muted-foreground pt-1 space-y-0.5 text-right">
                  <p><span className="text-destructive font-semibold">*</span> Either From/To Date & Time or Correlation ID is mandatory</p>
                  <p>From Date defaults to start of day (00:00), To Date defaults to end of day (23:59)</p>
                  <p>Min Response Time filters transactions with response time greater than the specified value</p>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {transactionData && (
            <>
              {Array.isArray(transactionData) && transactionData.length === 0 ? (
                <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                        <Search className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Found</h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any transactions matching your search criteria.
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="text-primary border-primary hover:bg-primary/10 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Clear Filters
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-foreground">
                        Transaction Results <span className="text-primary font-bold">{totalCount}</span> {totalCount === 1 ? 'record' : 'records'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="font-semibold">Sl No</TableHead>
                              <TableHead className="font-semibold">External Code</TableHead>
                              <TableHead className="font-semibold">Ref. No</TableHead>
                              <TableHead className="font-semibold">Full Name</TableHead>
                              <TableHead className="font-semibold">Channel</TableHead>
                              <TableHead className="font-semibold">Correlation ID</TableHead>
                              <TableHead className="font-semibold">Status</TableHead>
                              <TableHead className="font-semibold">Response Time (ms)</TableHead>
                              <TableHead className="font-semibold">Request At</TableHead>
                              <TableHead className="font-semibold">Response At</TableHead>
                              <TableHead className="font-semibold">AI Request At</TableHead>
                              <TableHead className="font-semibold">AI Response At</TableHead>
                              <TableHead className="font-semibold text-center">AI Telemetry</TableHead>
                              <TableHead className="font-semibold text-center">Conversation</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {transactionData?.map((row, index) => {
                              // Calculate serial number based on current page and index
                              const serialNumber = (currentPage - 1) * pageSize + index + 1;

                              // Get status badge variant
                              const getStatusBadge = (status: string) => {
                                const normalizedStatus = status?.toUpperCase() || '';
                                if (normalizedStatus.includes('SUCCESS') || normalizedStatus.includes('COMPLETED')) {
                                  return <Badge variant="default" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-400">{status}</Badge>;
                                } else if (normalizedStatus.includes('FAILED') || normalizedStatus.includes('ERROR')) {
                                  return <Badge variant="destructive">{status}</Badge>;
                                } else if (normalizedStatus.includes('PENDING') || normalizedStatus.includes('PROCESSING')) {
                                  return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20 dark:bg-yellow-500/20 dark:text-yellow-400">{status}</Badge>;
                                }
                                return <Badge variant="secondary">{status}</Badge>;
                              };

                              return (
                                <TableRow
                                  key={index}
                                  className="transition-colors hover:bg-muted/50 cursor-default"
                                >
                                  <TableCell className="font-medium">{serialNumber}</TableCell>
                                  <TableCell className="font-medium">{row.externalCd || '-'}</TableCell>
                                  <TableCell className="font-medium">{row.recipientId || '-'}</TableCell>
                                  <TableCell className="font-medium">{row.fullName || '-'}</TableCell>
                                  <TableCell>{row.channel || '-'}</TableCell>
                                  <TableCell className="font-mono text-xs">{row.correlationId || '-'}</TableCell>
                                  <TableCell>{getStatusBadge(row.status)}</TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <div className="font-semibold text-primary">
                                        Total: {formatNumber(row.responseTime)}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        AI: {formatNumber(row.aiResponseTime)}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.requestAt)}</TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.responseAt)}</TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.aiRequestAt)}</TableCell>
                                  <TableCell className="text-sm">{formatDateTime(row.aiResponseAt)}</TableCell>
                                  <TableCell className="text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleShowTelemetry(row.id)}
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                      title="View AI Telemetry"
                                    >
                                      <Info className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleShowConversation(row.id)}
                                      className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                      title="View Conversation"
                                    >
                                      <MessageSquare className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                    {/* Pagination matching dashboard style */}
                    <div className="flex items-center justify-between px-4 mt-4">
                      <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                        {totalCount} {totalCount === 1 ? 'record' : 'records'}
                      </div>
                      <div className="flex w-full items-center gap-8 lg:w-fit">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Rows per page</span>
                          <Select
                            value={pageSize.toString()}
                            onValueChange={(value) => {
                              const newSize = Number(value);
                              setSelectedPageSize(newSize);
                              if (currentFilter) {
                                executeQuery(currentFilter, 1, newSize);
                              } else {
                                setCurrentPage(1);
                              }
                            }}
                            disabled={isPending}
                          >
                            <SelectTrigger className="h-8 w-[70px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                                <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1 || isPending}
                              title="First page"
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1 || isPending}
                              title="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage >= Math.ceil(totalCount / pageSize) || isPending}
                              title="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                              disabled={currentPage >= Math.ceil(totalCount / pageSize) || isPending}
                              title="Last page"
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Telemetry Details Dialog */}
      <TelemetryDetailsDialog
        isOpen={telemetryDialogOpen}
        onClose={() => setTelemetryDialogOpen(false)}
        transactionId={selectedTransactionId}
      />

      {/* Conversation Details Dialog */}
      <ConversationDetailsDialog
        isOpen={conversationDialogOpen}
        onClose={() => setConversationDialogOpen(false)}
        transactionId={selectedTransactionId}
      />
    </div>
  );
};

export default TransactionTrackingPage;